import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, MessageSquare, BookOpen, LifeBuoy, Phone, Clock,
  ShieldCheck, Send, CheckCircle2, AlertCircle, ChevronDown, Sparkles,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { PainPoints, COMMS_STATS } from "@/components/welcome/PainPoints";
import { DiscChapters } from "@/components/welcome/DiscChapters";
import { SUPPORT_CHAPTERS } from "@/data/discChapters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_PATHS = [
  {
    Icon: BookOpen,
    title: "Knowledge base",
    desc: "Walkthroughs, setup guides and how-tos for every module.",
    cta: "Browse articles",
    href: "/kai", // best home for now (KAI / Manuals area)
    accent: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-500/12",
  },
  {
    Icon: MessageSquare,
    title: "Live chat",
    desc: "Talk to a human in under 60 seconds during clinic hours.",
    cta: "Start a chat",
    href: "#contact-form",
    accent: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500/12",
  },
  {
    Icon: Mail,
    title: "Email support",
    desc: "Reach the team at support@clinicfirst.io · we reply in < 4h.",
    cta: "support@clinicfirst.io",
    href: "mailto:support@clinicfirst.io",
    accent: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/12",
  },
  {
    Icon: Phone,
    title: "Phone (existing customers)",
    desc: "Already onboarded? Call your dedicated success line.",
    cta: "+1 (888) 555-0142",
    href: "tel:+18885550142",
    accent: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-500/12",
  },
];

const FAQS = [
  {
    q: "How fast does Clinic OS reply to support requests?",
    a: "Email: under 4 hours during business hours (8am-8pm ET, Mon-Fri). Live chat: under 60 seconds. Priority customers (Hub+ and Sales Autopilot) get a 15-minute SLA.",
  },
  {
    q: "Do you have onboarding help?",
    a: "Yes — every clinic gets a dedicated onboarding manager for the first 30 days. We typically go live in under 14 days from contract signature, including full data migration from your old stack.",
  },
  {
    q: "Where can I report a bug or request a feature?",
    a: "Use the contact form below and pick 'Bug' or 'Feature request' as the category. Bugs get triaged the same business day; feature requests are reviewed monthly by our product team.",
  },
  {
    q: "Is my patient data safe?",
    a: "Clinic OS is HIPAA-compliant and SOC 2 Type II audited. All patient data is encrypted in transit (TLS 1.3) and at rest (AES-256). We never share or sell your data.",
  },
  {
    q: "Can I export my data if I leave?",
    a: "Always. Your data is yours. Export every patient record, conversation, appointment and report at any time via the Settings panel or by emailing support.",
  },
  {
    q: "I haven't received a reply to my email — what now?",
    a: "Check your spam folder (replies sometimes land there). If still nothing after 4 business hours, ping us via live chat or email support@clinicfirst.io with 'URGENT' in the subject.",
  },
];

const CATEGORIES = [
  "Onboarding help",
  "Billing question",
  "Bug report",
  "Feature request",
  "Integration / EMR sync",
  "Sales question",
  "Something else",
];

export default function Support() {
  const openDemo = useDemo();
  const [form, setForm] = useState({
    name: "",
    email: "",
    clinic: "",
    category: CATEGORIES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in name, email and your message.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          clinic_name: form.clinic.trim() || "—",
          phone: form.phone?.trim() || "—",
          message: form.message.trim(),
          source: `support · ${form.category}`,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setSent(true);
    } catch (err) {
      setError("Couldn't send right now — please email support@clinicfirst.io directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article data-testid="support-page" className="overflow-hidden">
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-60" />
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b from-violet-500/15 via-blue-500/10 to-transparent blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-8 sm:pt-40 sm:pb-20 lg:px-12">
          <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
            <LifeBuoy className="h-3.5 w-3.5 text-violet-500" />
            Support · we&apos;ve got you
          </p>
          <h1 className="mt-0 sm:mt-6 max-w-4xl font-display text-[2.05rem] font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            How can we{" "}
            <span className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 bg-clip-text italic text-transparent">help today?</span>
          </h1>
          <p className="mt-3 sm:mt-5 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[17px]">
            Pick a path below — knowledge base, live chat, email or phone — or send us a message and we&apos;ll reply within 4 hours during business hours.
          </p>

          {/* SLA / reassurance strip — desktop only */}
          <div className="mt-7 hidden flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-emerald-500" />
              <span className="font-bold text-foreground/85">&lt; 4h</span> avg email reply
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-emerald-500" />
              <span className="font-bold text-foreground/85">&lt; 60s</span> live chat
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              HIPAA &amp; SOC 2 compliant
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <Link to="/status" data-testid="support-status-link" className="inline-flex items-center gap-1.5 hover:text-foreground">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              All systems operational
            </Link>
          </div>
        </div>
      </section>

      {/* ───── DiSC PAIN-MAPPING ───── */}
      <div className="border-t border-border/60 pt-10 sm:pt-14">
        <PainPoints
          eyebrow="When you need a human"
          heading="The numbers behind our support promise."
          sub="Industry tickets sit. Ours don&apos;t. These are the response gaps Clinic OS support closes."
          stats={COMMS_STATS}
        />
        <DiscChapters
          eyebrow="Support, four ways"
          heading="Pick the chapter that sounds like your concern."
          sub="Speed, humans, hand-holding, audit — every support promise we make, told four ways."
          chapters={SUPPORT_CHAPTERS}
        />
      </div>

      {/* QUICK PATHS */}
      <section data-testid="support-quick-paths" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Fastest paths to a human
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Pick the way that works for you.
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {QUICK_PATHS.map((p, i) => {
              const internal = p.href.startsWith("/");
              const Tag = internal ? Link : "a";
              const props = internal ? { to: p.href } : { href: p.href };
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Tag
                    {...props}
                    data-testid={`support-path-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/30 hover:shadow-lg"
                  >
                    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${p.bg} ${p.accent}`}>
                      <p.Icon className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-display text-base font-black tracking-tight">{p.title}</p>
                    <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-muted-foreground">{p.desc}</p>
                    <span className={`mt-4 inline-flex items-center gap-1 text-[12px] font-bold ${p.accent}`}>
                      {p.cta}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Tag>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT FORM + FAQ */}
      <section
        id="contact-form"
        className="border-t border-border bg-gradient-to-b from-violet-50/40 via-blue-50/25 to-emerald-50/30 py-20 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-28"
      >
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14">
            {/* LEFT — Form */}
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
                <div className="border-b border-border bg-gradient-to-r from-violet-500/8 via-blue-500/5 to-emerald-500/8 px-6 py-4">
                  <p className="font-display text-sm font-black tracking-tight">
                    Send us a message
                  </p>
                  <p className="text-[12.5px] text-muted-foreground">
                    We reply within 4 business hours.
                  </p>
                </div>
                <div className="p-6 sm:p-8">
                  <AnimatePresence mode="wait">
                    {!sent ? (
                      <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        data-testid="support-contact-form"
                        className="space-y-4"
                      >
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Your name" required>
                            <input
                              type="text"
                              value={form.name}
                              onChange={(e) => update("name", e.target.value)}
                              data-testid="support-input-name"
                              placeholder="Dr. Carter"
                              className="block w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-violet-500/60"
                            />
                          </Field>
                          <Field label="Email" required>
                            <input
                              type="email"
                              value={form.email}
                              onChange={(e) => update("email", e.target.value)}
                              data-testid="support-input-email"
                              placeholder="you@clinic.com"
                              className="block w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-violet-500/60"
                            />
                          </Field>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Clinic name">
                            <input
                              type="text"
                              value={form.clinic}
                              onChange={(e) => update("clinic", e.target.value)}
                              data-testid="support-input-clinic"
                              placeholder="Hunt Club Physio"
                              className="block w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-violet-500/60"
                            />
                          </Field>
                          <Field label="What's this about?" required>
                            <select
                              value={form.category}
                              onChange={(e) => update("category", e.target.value)}
                              data-testid="support-input-category"
                              className="block w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-violet-500/60"
                            >
                              {CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </Field>
                        </div>
                        <Field label="Your message" required>
                          <textarea
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            data-testid="support-input-message"
                            rows={5}
                            placeholder="Tell us what's happening, and which clinic / module if relevant…"
                            className="block w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-violet-500/60"
                          />
                        </Field>

                        {error && (
                          <p className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rose-500">
                            <AlertCircle className="h-3.5 w-3.5" /> {error}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                          <p className="text-[11px] text-muted-foreground">
                            By submitting, you agree to our reasonable reply window of &lt;4 business hours.
                          </p>
                          <button
                            type="submit"
                            disabled={submitting}
                            data-testid="support-submit"
                            className="inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-5 text-sm font-bold text-white shadow-lg shadow-violet-600/30 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                          >
                            {submitting ? "Sending…" : (
                              <>
                                <Send className="h-4 w-4" /> Send message
                              </>
                            )}
                          </button>
                        </div>
                      </motion.form>
                    ) : (
                      <motion.div
                        key="sent"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        data-testid="support-sent"
                        className="rounded-2xl border border-emerald-500/30 bg-emerald-500/8 p-6 text-center"
                      >
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-6 w-6" />
                        </span>
                        <h3 className="mt-4 font-display text-xl font-black tracking-tight">
                          Got it — we&apos;ll reply within 4 hours.
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                          Sent to <span className="font-semibold text-foreground/85">{form.email}</span>.
                          A confirmation will land in your inbox shortly. If something&apos;s urgent, email{" "}
                          <a href="mailto:support@clinicfirst.io" className="font-semibold text-foreground underline-offset-2 hover:underline">
                            support@clinicfirst.io
                          </a>{" "}
                          with &ldquo;URGENT&rdquo; in the subject.
                        </p>
                        <button
                          type="button"
                          onClick={() => { setSent(false); setForm({ name: "", email: "", clinic: "", category: CATEGORIES[0], message: "" }); }}
                          data-testid="support-send-another"
                          className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-violet-600 hover:text-violet-700 dark:text-violet-300"
                        >
                          Send another message <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* RIGHT — FAQ */}
            <div className="lg:col-span-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                FAQ
              </p>
              <h2 className="mt-3 font-display text-2xl font-black tracking-tight sm:text-3xl">
                Common questions.
              </h2>

              <div className="mt-5 divide-y divide-border rounded-2xl border border-border bg-card">
                {FAQS.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <button
                      key={f.q}
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      data-testid={`support-faq-${i}`}
                      className="block w-full text-left transition-colors hover:bg-secondary/40"
                    >
                      <div className="flex items-start gap-3 px-5 py-4">
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-[14px] font-bold leading-snug tracking-tight">
                            {f.q}
                          </p>
                          <AnimatePresence initial={false}>
                            {open && (
                              <motion.p
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.25 }}
                                className="mt-2 text-[13px] leading-relaxed text-muted-foreground"
                              >
                                {f.a}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </div>
                        <ChevronDown
                          className={`mt-0.5 h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Demo nudge */}
              <div className="mt-6 rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/10 to-blue-500/5 p-5">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-md shadow-violet-500/30">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-display text-sm font-black tracking-tight">Not a customer yet?</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                      The fastest way to evaluate Clinic OS is a 15-min walkthrough. We&apos;ll show your stack on screen.
                    </p>
                    <button
                      type="button"
                      onClick={openDemo}
                      data-testid="support-book-demo"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-violet-600 hover:text-violet-700 dark:text-violet-300"
                    >
                      Book a demo <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </article>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block text-left">
    <span className="mb-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
  </label>
);
